/**
 * Matcha contact form — fallback email validation + one-time Shopify Forms submit upgrade.
 * Avoids MutationObserver and shadow-DOM mutations (those caused load freezes with Forms app).
 */
class MatchaContactForm extends HTMLElement {
  #form;
  #emailInput;
  #errorEl;
  #formsRetryTimer;

  #emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  #formsRetryCount = 0;
  #maxFormsRetries = 25;
  #successWatchTimer = null;
  #successWatchAttempts = 0;
  #maxSuccessWatchAttempts = 40;

  connectedCallback() {
    if (this.dataset.matchaInit === 'true') return;
    this.dataset.matchaInit = 'true';

    this.#form = this.querySelector('form.matcha-contact-form__form');

    if (this.#form) {
      this.#emailInput = this.#form.querySelector('[data-matcha-contact-email]');
      this.#errorEl = this.#form.querySelector('[data-matcha-contact-email-error]');
      this.#form.addEventListener('submit', this.#onFallbackSubmit);
      this.#emailInput?.addEventListener('input', this.#clearEmailError);
      this.#emailInput?.addEventListener('blur', this.#validateFallbackEmail);
      this.#initFallbackFloatFields();
    }

    if (this.querySelector('.matcha-contact-form__app')) {
      this.#scheduleFormsSubmitUpgrade();
    }
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onFallbackSubmit);
    this.#emailInput?.removeEventListener('input', this.#clearEmailError);
    this.#emailInput?.removeEventListener('blur', this.#validateFallbackEmail);

    if (this.#formsRetryTimer) {
      clearTimeout(this.#formsRetryTimer);
      this.#formsRetryTimer = null;
    }

    if (this.#successWatchTimer) {
      clearTimeout(this.#successWatchTimer);
      this.#successWatchTimer = null;
    }
  }

  #onFallbackSubmit = (event) => {
    const nameField = this.#form?.querySelector('[data-matcha-contact-name]');
    const firstName = this.#form?.querySelector('[data-matcha-first-name]');
    const lastName = this.#form?.querySelector('[data-matcha-last-name]');

    if (nameField && (firstName || lastName)) {
      const fullName = [firstName?.value.trim(), lastName?.value.trim()].filter(Boolean).join(' ');
      nameField.value = fullName;
    }

    if (!this.#validateFallbackEmail()) {
      event.preventDefault();
      this.#emailInput?.focus();
    }
  };

  #clearEmailError = () => {
    if (!this.#emailInput || !this.#errorEl) return;
    if (this.#emailInput.getAttribute('aria-invalid') !== 'true') return;
    if (this.#emailPattern.test(this.#emailInput.value.trim())) {
      this.#emailInput.removeAttribute('aria-invalid');
      this.#errorEl.hidden = true;
      this.#errorEl.textContent = '';
    }
  };

  #validateFallbackEmail = () => {
    if (!this.#emailInput || !this.#errorEl) return true;

    const value = this.#emailInput.value.trim();
    if (value === '' || !this.#emailPattern.test(value)) {
      this.#emailInput.setAttribute('aria-invalid', 'true');
      this.#errorEl.hidden = false;
      this.#errorEl.textContent = this.#emailInput.dataset.errorMessage || 'Please enter a valid email address.';
      return false;
    }

    this.#emailInput.removeAttribute('aria-invalid');
    this.#errorEl.hidden = true;
    this.#errorEl.textContent = '';
    return true;
  };

  #escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #buildWipeLabel(label) {
    const safe = this.#escapeHtml(label);
    return `<span class="matcha-wipe-btn__label"><span class="matcha-wipe-btn__label-base">${safe}</span><span class="matcha-wipe-btn__label-invert-wrap" aria-hidden="true"><span class="matcha-wipe-btn__label-invert">${safe}</span></span></span>`;
  }

  #findFormsShadowRoot() {
    const app = this.querySelector('.matcha-contact-form__app');
    if (!app) return null;

    const embed = app.querySelector('shopify-forms-embed, form-embed, #app-embed');
    return embed?.shadowRoot || null;
  }

  #getFieldBackgroundColor() {
    return this.dataset.fieldBg || '#F7F2EC';
  }

  #ensureFieldVisible(field) {
    if (!field) return;

    field.style.setProperty('opacity', '1', 'important');
    field.style.setProperty('visibility', 'visible', 'important');

    if (field.matches('[role="combobox"], button[aria-haspopup="listbox"]')) {
      field.style.setProperty('display', 'flex', 'important');
    } else if (field.matches('select, input, textarea')) {
      field.style.setProperty('display', 'block', 'important');
    }
  }

  #ensureAncestorsVisible(field, form) {
    let ancestor = field;
    while (ancestor && ancestor !== form) {
      ancestor.style.setProperty('overflow', 'visible', 'important');
      ancestor.style.setProperty('height', 'auto', 'important');
      ancestor.style.setProperty('max-height', 'none', 'important');
      ancestor.style.setProperty('opacity', '1', 'important');
      ancestor.style.setProperty('visibility', 'visible', 'important');
      ancestor = ancestor.parentElement;
    }
  }

  #hideFieldLabels(fieldRoot, trigger) {
    if (!fieldRoot) return;

    fieldRoot
      .querySelectorAll(
        'label, legend, [class*="FloatingLabel" i], [class*="floating-label" i], [class*="floatingLabel" i], [class*="FieldLabel" i], [class*="field-label" i]'
      )
      .forEach((label) => {
        if (trigger && (label === trigger || trigger.contains(label))) return;
        this.#markLabelHidden(label);
      });
  }

  #hideOverlayElement(element) {
    if (!element) return;
    if (
      element.matches(
        '[data-matcha-select-value="true"], [role="combobox"], button[aria-haspopup="listbox"], select, input, textarea'
      )
    ) {
      return;
    }
    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('display', 'none', 'important');
  }

  #cleanLabelText(text) {
    return String(text || '')
      .replace(/\s*\*\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #findFieldRoot(field, form) {
    let node = field;

    while (node?.parentElement && node.parentElement !== form) {
      node = node.parentElement;
    }

    return node?.parentElement === form ? node : field.parentElement;
  }

  #isPhoneInput(field) {
    if (!(field instanceof HTMLInputElement)) return false;

    const type = (field.getAttribute('type') || '').toLowerCase();
    if (type === 'tel') return true;

    const autocomplete = field.getAttribute('autocomplete') || '';
    if (autocomplete === 'tel' || autocomplete === 'tel-national' || autocomplete === 'tel-local') return true;

    const name = (field.getAttribute('name') || field.id || '').toLowerCase();
    return /phone|tel|mobile|telefoon/.test(name);
  }

  #isPhoneFieldGroupRoot(root) {
    if (!root) return false;

    const telInput = root.querySelector('input[type="tel"]');
    if (!telInput) return false;

    return Boolean(
      root.querySelector(
        'button, [role="combobox"], button[aria-haspopup="listbox"], select, [class*="country" i], [class*="Country" i], [class*="flag" i], [class*="Flag" i], [class*="dial" i], img[alt*="flag" i]'
      )
    );
  }

  #isPhoneCountryPart(field, form) {
    if (this.#isPhoneInput(field)) return false;

    const outerRoot = this.#findFieldRoot(field, form);
    return this.#isPhoneFieldGroupRoot(outerRoot);
  }

  #ensurePhoneFloatWrapper(field, outerRoot) {
    if (!field || !outerRoot || field.parentElement !== outerRoot) return field.parentElement;

    let wrapper = outerRoot.querySelector(':scope > [data-matcha-phone-input-wrap="true"]');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.setAttribute('data-matcha-phone-input-wrap', 'true');
      outerRoot.insertBefore(wrapper, field);
      wrapper.appendChild(field);
    }

    return wrapper;
  }

  #findB2bColumnRoot(field, formChildRoot, form) {
    if (!field || !formChildRoot || !formChildRoot.contains(field)) return null;

    const countLabelable = (root) =>
      [
        ...root.querySelectorAll(
          'input:not([type="hidden"]), textarea, select, [role="combobox"], button[aria-haspopup="listbox"]'
        ),
      ].filter((input) => !this.#isPhoneCountryPart(input, form)).length;

    if (countLabelable(formChildRoot) <= 1) return null;

    let node = field.parentElement;
    let best = null;

    while (node && node !== formChildRoot) {
      const fields = [
        ...node.querySelectorAll(
          'input:not([type="hidden"]), textarea, select, [role="combobox"], button[aria-haspopup="listbox"]'
        ),
      ].filter((input) => !this.#isPhoneCountryPart(input, form));

      if (fields.length === 1 && fields[0] === field) {
        best = node;
      }

      node = node.parentElement;
    }

    return best;
  }

  #findFieldHintScope(field, form) {
    const formChildRoot = this.#findFieldRoot(field, form);

    if (this.#isB2bForm() && formChildRoot) {
      const columnRoot = this.#findB2bColumnRoot(field, formChildRoot, form);
      if (columnRoot) return columnRoot;
    }

    return formChildRoot || field.parentElement;
  }

  #findFloatRoot(field, form) {
    if (this.#isPhoneInput(field)) {
      const outerRoot = this.#findFieldRoot(field, form);

      if (this.#isPhoneFieldGroupRoot(outerRoot)) {
        if (field.parentElement === outerRoot) {
          return this.#ensurePhoneFloatWrapper(field, outerRoot);
        }

        if (field.parentElement && field.parentElement !== outerRoot) {
          return field.parentElement;
        }
      }
    }

    const formChildRoot = this.#findFieldRoot(field, form);
    if (this.#isB2bForm() && formChildRoot) {
      const columnRoot = this.#findB2bColumnRoot(field, formChildRoot, form);
      if (columnRoot) return columnRoot;
    }

    return formChildRoot || field.parentElement;
  }

  #cleanupPhoneFloatShell(outerRoot, innerRoot) {
    if (!outerRoot || !innerRoot || outerRoot === innerRoot) return;

    if (outerRoot.hasAttribute('data-matcha-float-field')) {
      outerRoot.removeAttribute('data-matcha-float-field');
      outerRoot.removeAttribute('data-matcha-float-active');
      outerRoot.querySelector(':scope > .matcha-float-label')?.remove();
    }
  }

  #unwrapFieldContainer(field, form) {
    this.#ensureAncestorsVisible(field, form);
  }

  #unwrapSelectContainer(field, form) {
    const fieldRoot = this.#findFieldRoot(field, form);
    this.#ensureAncestorsVisible(field, form);
    this.#hideFieldLabels(fieldRoot, field);
  }

  #getFieldLabelText(field, form) {
    const placeholder = this.#cleanLabelText(field.getAttribute('placeholder'));
    if (placeholder) return placeholder;

    return this.#getFieldHintText(field, form);
  }

  #getSelectLabelText(field, form) {
    return this.#getFieldHintText(field, form);
  }

  #getFieldHintText(field, form) {
    const fieldId = field.getAttribute('id');
    if (fieldId) {
      const linkedLabel = form.querySelector(`label[for="${CSS.escape(fieldId)}"]`);
      const linkedText = this.#cleanLabelText(linkedLabel?.textContent);
      if (linkedText) return linkedText;
    }

    const associatedLabel = this.#findAssociatedLabel(field, form);
    if (associatedLabel) {
      const linkedText = this.#cleanLabelText(associatedLabel.textContent);
      if (linkedText) return linkedText;
    }

    const nativeHints = this.#findFieldHintElements(field, form);
    for (const hint of nativeHints) {
      const nativeText = this.#cleanLabelText(hint.textContent);
      if (nativeText) return nativeText;
    }

    const labelledBy = field.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labelNode = form.querySelector(`#${CSS.escape(labelledBy.split(' ')[0])}`);
      const labelledText = this.#cleanLabelText(labelNode?.textContent);
      if (labelledText) return labelledText;
    }

    const rawPlaceholder = field.getAttribute('placeholder') || '';
    const placeholder = this.#cleanLabelText(rawPlaceholder);
    if (placeholder && !/^\s+$/.test(rawPlaceholder)) return placeholder;

    const ariaLabel = this.#cleanLabelText(field.getAttribute('aria-label'));
    if (ariaLabel) return ariaLabel;

    const floatingLabel = this.#getFloatingLabelText(field);
    if (floatingLabel) return floatingLabel;

    const existingFloat = field
      .closest('[data-matcha-float-field]')
      ?.querySelector(':scope > .matcha-float-label');
    const existingFloatText = this.#cleanLabelText(existingFloat?.textContent);
    if (existingFloatText) return existingFloatText;

    return this.#getPlaceholderFallback(field);
  }

  #findAssociatedLabel(field, form) {
    const scope = this.#findFieldHintScope(field, form) || form;
    const labelParent = field.closest('label');
    if (labelParent && scope.contains(labelParent)) return labelParent;

    let root = field.parentElement;
    while (root && root !== scope && root !== form) {
      const directLabel = root.querySelector(':scope > label');
      if (directLabel && !directLabel.contains(field) && scope.contains(directLabel)) return directLabel;

      const siblingLabel = field.previousElementSibling;
      if (siblingLabel?.matches('label') && scope.contains(siblingLabel)) return siblingLabel;

      root = root.parentElement;
    }

    return null;
  }

  #getFloatingLabelText(field) {
    const fieldRoot = field.parentElement;
    if (!fieldRoot) return '';

    const floatLabel = fieldRoot.querySelector(
      ':scope > label, :scope > [class*="FloatingLabel" i], :scope > [class*="floating-label" i], :scope > [class*="floatingLabel" i], :scope > [class*="FieldLabel" i], :scope > [class*="field-label" i]'
    );

    if (floatLabel && floatLabel !== field && !floatLabel.contains(field) && !field.contains(floatLabel)) {
      return this.#cleanLabelText(floatLabel.textContent);
    }

    return '';
  }

  #getPlaceholderFallback(field) {
    const autocomplete = field.getAttribute('autocomplete') || '';
    const autocompleteMap = {
      'given-name': 'First Name',
      'family-name': 'Last Name',
      email: 'Email',
      tel: 'Phone',
    };
    if (autocompleteMap[autocomplete]) return autocompleteMap[autocomplete];

    const name = (field.getAttribute('name') || field.id || '').toLowerCase();
    if (/first.?name|fname|given|voornaam|firstname/.test(name)) return 'First Name';
    if (/last.?name|lname|family|surname|achternaam|lastname/.test(name)) return 'Last Name';
    if (/email|e-mail|mail/.test(name)) return 'Email';
    if (/phone|tel|mobile|telefoon/.test(name)) return 'Phone';
    if (/message|body|comment|note|bericht/.test(name)) return 'Your message';

    if (field.getAttribute('type') === 'email') return 'Email';

    return '';
  }

  #normalizeFieldLabel(text, field) {
    const raw = this.#cleanLabelText(text);
    const name = (field?.getAttribute('name') || field?.id || '').toLowerCase();

    if (raw === 'firstName' || raw === 'firstname' || /^first\s*name$/i.test(raw)) return 'First Name';
    if (raw === 'lastName' || raw === 'lastname' || /^last\s*name$/i.test(raw)) return 'Last Name';
    if (/^voornaam$/i.test(raw)) return 'First Name';
    if (/^achternaam$/i.test(raw)) return 'Last Name';

    if (/first.?name|fname|given|voornaam|firstname/.test(name)) return 'First Name';
    if (/last.?name|lname|family|surname|achternaam|lastname/.test(name)) return 'Last Name';

    if (raw && /[a-z][A-Z]/.test(raw)) {
      return raw
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return raw;
  }

  #fieldHasValue(field) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return false;

    const value = String(field.value ?? '').trim();
    if (value.length > 0) return true;

    return field.dataset.matchaFloatFilled === 'true';
  }

  #isB2bForm() {
    return this.classList.contains('matcha-contact-form--b2b-page');
  }

  #isCountryOnlyRoot(root) {
    if (!root) return false;

    const hasCombobox = root.querySelector('select, [role="combobox"], button[aria-haspopup="listbox"]');
    if (!hasCombobox) return false;

    return !root.querySelector('input:not([type="hidden"]), textarea');
  }

  #resolveFieldLabel(field, form) {
    return this.#normalizeFieldLabel(this.#getFieldHintText(field, form), field);
  }

  #findLabeledField(fieldByLabel, labels) {
    for (const label of labels) {
      if (fieldByLabel.has(label)) return fieldByLabel.get(label);
    }

    const normalized = new Map();
    for (const [key, field] of fieldByLabel) {
      normalized.set(key.toLowerCase(), field);
    }

    for (const label of labels) {
      const hit = normalized.get(label.toLowerCase());
      if (hit) return hit;
    }

    return null;
  }

  #buildFieldLabelIndex(form) {
    const fieldByLabel = new Map();
    if (!form) return fieldByLabel;

    form
      .querySelectorAll(
        'input:not([type="hidden"]), textarea, select, [role="combobox"], button[aria-haspopup="listbox"]'
      )
      .forEach((field) => {
        if (this.#isPhoneCountryPart(field, form)) return;

        const label = this.#resolveFieldLabel(field, form);
        if (!label || fieldByLabel.has(label)) return;

        fieldByLabel.set(label, field);
      });

    return fieldByLabel;
  }

  #syncFloatFieldState(root, field) {
    if (!root || !field) return;

    if (root.hasAttribute('data-matcha-float-static')) {
      root.setAttribute('data-matcha-float-active', 'true');
      return;
    }

    let active = field === document.activeElement;

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      const hasValue = this.#fieldHasValue(field);
      if (hasValue) {
        field.dataset.matchaFloatFilled = 'true';
      } else {
        delete field.dataset.matchaFloatFilled;
      }
      active = active || hasValue;
    } else if (field.matches('select, [role="combobox"], button[aria-haspopup="listbox"]')) {
      active = active || field.getAttribute('data-matcha-empty') !== 'true';
    }

    if (active) {
      root.setAttribute('data-matcha-float-active', 'true');
    } else {
      root.removeAttribute('data-matcha-float-active');
    }
  }

  #syncAllFloatFields(form) {
    if (!form) return;

    form.querySelectorAll('[data-matcha-float-field]').forEach((root) => {
      const field = root.querySelector(
        'input:not([type="hidden"]), textarea, select, [role="combobox"], button[aria-haspopup="listbox"]'
      );
      if (field) this.#syncFloatFieldState(root, field);
    });
  }

  #ensureFloatLabel(fieldRoot, labelText, field, options = {}) {
    if (!fieldRoot || !labelText) return;

    const { isTextarea = false, isPhone = false } = options;

    fieldRoot.setAttribute('data-matcha-float-field', 'true');
    if (isTextarea) {
      fieldRoot.setAttribute('data-matcha-float-textarea', 'true');
    }
    if (isPhone) {
      fieldRoot.setAttribute('data-matcha-float-phone', 'true');
      fieldRoot.setAttribute('data-matcha-float-static', 'true');
    } else {
      fieldRoot.removeAttribute('data-matcha-float-phone');
      fieldRoot.removeAttribute('data-matcha-float-static');
    }

    let floatLabel = fieldRoot.querySelector(':scope > .matcha-float-label');
    if (!floatLabel) {
      floatLabel = document.createElement('span');
      floatLabel.className = 'matcha-float-label';
      fieldRoot.appendChild(floatLabel);
    }
    floatLabel.textContent = labelText;

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      field.setAttribute('placeholder', ' ');
      field.removeAttribute('data-matcha-hint-mode');
      field.setAttribute('aria-label', labelText);
    } else {
      field.setAttribute('aria-label', labelText);
    }

    this.#syncFloatFieldState(fieldRoot, field);
  }

  #applyB2bFieldPair(form, fieldA, fieldB) {
    const rootA = this.#findFloatRoot(fieldA, form) || this.#findFieldRoot(fieldA, form);
    const rootB = this.#findFloatRoot(fieldB, form) || this.#findFieldRoot(fieldB, form);
    if (!rootA || !rootB) return false;

    if (rootA === rootB) {
      rootA.setAttribute('data-matcha-b2b-row', 'pair');
      return true;
    }

    if (rootA.closest('[data-matcha-b2b-row="pair"]')?.contains(rootB)) return true;

    const parent = rootA.parentElement;
    if (parent && parent === rootB.parentElement) {
      if (parent.getAttribute('data-matcha-b2b-row') === 'pair') return true;

      const parentStyle = window.getComputedStyle(parent);
      const alreadyRow =
        parentStyle.display === 'flex' ||
        parentStyle.display === 'inline-flex' ||
        (parentStyle.display === 'grid' && parentStyle.gridTemplateColumns !== 'none');

      if (alreadyRow) {
        parent.setAttribute('data-matcha-b2b-row', 'pair');
        return true;
      }

      parent.setAttribute('data-matcha-b2b-row', 'pair');
      return true;
    }

    return false;
  }

  #setupB2bFieldRows(form) {
    if (!this.#isB2bForm() || !form) return;

    const fieldByLabel = this.#buildFieldLabelIndex(form);
    const pairs = [
      [['First Name', 'First name'], ['Last Name', 'Last name']],
      [['Postal code', 'Postal Code', 'Postcode'], ['City']],
    ];

    for (const [leftLabels, rightLabels] of pairs) {
      const leftField = this.#findLabeledField(fieldByLabel, leftLabels);
      const rightField = this.#findLabeledField(fieldByLabel, rightLabels);
      if (!leftField || !rightField) continue;

      this.#applyB2bFieldPair(form, leftField, rightField);
    }
  }

  #setupFloatingLabels(form) {
    if (!form) return;

    form.querySelectorAll('input:not([type="hidden"]), textarea').forEach((field) => {
      if (this.#isPhoneCountryPart(field, form)) {
        this.#hideFieldHintElements(field, form);
        return;
      }

      const isPhone = this.#isPhoneInput(field);
      const fieldRoot = this.#findFloatRoot(field, form);
      if (!fieldRoot) return;

      if (isPhone) {
        this.#cleanupPhoneFloatShell(this.#findFieldRoot(field, form), fieldRoot);
      }

      const labelText = this.#resolveFieldLabel(field, form);
      if (!labelText) return;

      this.#hideFieldHintElements(field, form);
      this.#ensureFloatLabel(fieldRoot, labelText, field, {
        isTextarea: field.tagName === 'TEXTAREA',
        isPhone,
      });
    });

    form.querySelectorAll('select, [role="combobox"], button[aria-haspopup="listbox"]').forEach((field) => {
      if (this.#isPhoneCountryPart(field, form)) {
        this.#hideFieldHintElements(field, form);
        return;
      }

      const fieldRoot = this.#findFloatRoot(field, form) || field.parentElement;
      if (!fieldRoot) return;

      const labelText = this.#resolveFieldLabel(field, form);
      if (!labelText) return;

      this.#hideFieldHintElements(field, form);
      this.#ensureFloatLabel(fieldRoot, labelText, field);
    });

    this.#bindFloatingLabelInteractions(form);
    this.#syncAllFloatFields(form);
    this.#setupB2bFieldRows(form);
  }

  #bindFloatingLabelInteractions(form) {
    if (!form || form.dataset.matchaFloatBound === 'true') return;

    const sync = (field) => {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement ||
          field instanceof HTMLSelectElement ||
          field.matches?.('[role="combobox"], button[aria-haspopup="listbox"]')
        )
      ) {
        return;
      }

      const root = field.closest('[data-matcha-float-field]');
      if (root) this.#syncFloatFieldState(root, field);
    };

    form.addEventListener('input', (event) => sync(event.target), true);
    form.addEventListener('change', (event) => sync(event.target), true);
    form.addEventListener('focusin', (event) => sync(event.target), true);
    form.addEventListener(
      'focusout',
      (event) => {
        const field = event.target;
        sync(field);
        window.setTimeout(() => sync(field), 0);
        window.setTimeout(() => sync(field), 50);
      },
      true
    );

    form.dataset.matchaFloatBound = 'true';
  }

  #initFallbackFloatFields() {
    this.querySelectorAll('.matcha-float-field').forEach((root) => {
      const field = root.querySelector('input, textarea, select');
      if (!field) return;
      this.#syncFloatFieldState(root, field);
    });

    const form = this.#form;
    if (!form || form.dataset.matchaFallbackFloatBound === 'true') return;

    const sync = (field) => {
      const root = field?.closest?.('.matcha-float-field, [data-matcha-float-field]');
      if (
        root &&
        (field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement ||
          field instanceof HTMLSelectElement)
      ) {
        this.#syncFloatFieldState(root, field);
      }
    };

    form.addEventListener('input', (event) => sync(event.target), true);
    form.addEventListener('change', (event) => sync(event.target), true);
    form.addEventListener('focusin', (event) => sync(event.target), true);
    form.addEventListener(
      'focusout',
      (event) => {
        const field = event.target;
        sync(field);
        window.setTimeout(() => sync(field), 0);
        window.setTimeout(() => sync(field), 50);
      },
      true
    );
    form.dataset.matchaFallbackFloatBound = 'true';
  }

  #refreshFloatingLabels(form) {
    if (!form) return;
    this.#setupFloatingLabels(form);
    this.#syncAllFloatFields(form);
  }

  #findFieldHintElements(field, form) {
    const fieldRoot = this.#findFieldHintScope(field, form);
    if (!fieldRoot) return [];

    return Array.from(
      fieldRoot.querySelectorAll(
        'label, legend, [class*="FloatingLabel" i], [class*="floating-label" i], [class*="floatingLabel" i], [class*="FieldLabel" i], [class*="field-label" i]'
      )
    ).filter(
      (el) =>
        el !== field &&
        !field.contains(el) &&
        !el.contains(field) &&
        !el.matches('[data-matcha-select-value="true"], [role="listbox"], [role="option"], [role="combobox"], button[aria-haspopup="listbox"]')
    );
  }

  #hideFieldHintElements(field, form) {
    this.#findFieldHintElements(field, form).forEach((el) => this.#markLabelHidden(el));
  }

  #markLabelHidden(element) {
    if (!element) return;
    element.setAttribute('data-matcha-label-hidden', 'true');
    element.setAttribute('aria-hidden', 'true');
  }

  #prepareSelectField(field, form) {
    if (this.#isPhoneCountryPart(field, form)) {
      this.#hideFieldHintElements(field, form);
      return;
    }

    const fieldRoot = this.#findFloatRoot(field, form) || this.#findFieldRoot(field, form);
    const labelText = this.#normalizeFieldLabel(this.#getSelectLabelText(field, form), field);

    this.#hideFieldHintElements(field, form);

    if (fieldRoot && labelText) {
      this.#ensureFloatLabel(fieldRoot, labelText, field);
    }

    if (field.matches('select') && labelText && field.options.length > 0) {
      const firstOption = field.options[0];
      if (!firstOption.value || firstOption.disabled) {
        firstOption.textContent = labelText;
      }
    }

    if (field.matches('[role="combobox"], button[aria-haspopup="listbox"]')) {
      const valueEl = this.#getComboboxValueElement(field);
      if (valueEl) {
        valueEl.setAttribute('data-matcha-select-value', 'true');
      }
    }

    this.#syncSelectEmptyState(field, labelText);
  }

  #getComboboxValueElement(combobox) {
    const marked = combobox.querySelector('[data-matcha-select-value="true"]');
    if (marked) return marked;

    for (const child of combobox.children) {
      if (child.matches('svg, [class*="icon" i], [class*="Icon" i], [class*="chevron" i]')) continue;
      if (child.matches('span, div, p')) return child;
    }

    return null;
  }

  #syncSelectEmptyState(field, labelText) {
    if (!field) return;

    if (field.matches('select')) {
      const selected = field.options[field.selectedIndex];
      const value = selected?.value?.trim() || '';
      const text = this.#cleanLabelText(selected?.textContent || '');
      const isEmpty =
        !value ||
        selected?.disabled ||
        text === labelText ||
        /^select\b/i.test(text) ||
        /^choose\b/i.test(text) ||
        /^kies\b/i.test(text);

      if (isEmpty) {
        field.setAttribute('data-matcha-empty', 'true');
      } else {
        field.removeAttribute('data-matcha-empty');
      }

      const root = field.closest('[data-matcha-float-field]');
      if (root) this.#syncFloatFieldState(root, field);
      return;
    }

    const valueEl = this.#getComboboxValueElement(field);
    const displayText = this.#cleanLabelText(valueEl?.textContent || field.textContent);
    const isEmpty =
      !displayText ||
      displayText === labelText ||
      /^select\b/i.test(displayText) ||
      /^choose\b/i.test(displayText) ||
      /^kies\b/i.test(displayText);

    if (isEmpty) {
      field.setAttribute('data-matcha-empty', 'true');
    } else {
      field.removeAttribute('data-matcha-empty');
    }

    const root = field.closest('[data-matcha-float-field]');
    if (root) this.#syncFloatFieldState(root, field);
  }

  #bindSelectFieldInteractions(form) {
    if (!form || form.dataset.matchaSelectInteractions === 'true') return;

    form.addEventListener('change', (event) => {
      const select = event.target.closest('select');
      if (!select || !form.contains(select)) return;
      this.#syncSelectEmptyState(select, this.#getSelectLabelText(select, form));
    });

    form.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option || !form.contains(option)) return;

        const listbox = option.closest('[role="listbox"]');
        const previous = listbox?.previousElementSibling;
        const combobox =
          (listbox?.id
            ? form.querySelector(
                `[role="combobox"][aria-controls="${CSS.escape(listbox.id)}"], button[aria-haspopup="listbox"][aria-controls="${CSS.escape(listbox.id)}"]`
              )
            : null) ||
          (previous?.matches?.('[role="combobox"], button[aria-haspopup="listbox"]') ? previous : null) ||
          this.#findComboboxForListbox(listbox, form);

        if (combobox instanceof HTMLElement) {
          window.setTimeout(() => {
            this.#syncSelectEmptyState(combobox, this.#getSelectLabelText(combobox, form));
          }, 0);
        }
      },
      true
    );

    form.addEventListener('focusout', (event) => {
      const trigger = event.target.closest('[role="combobox"], button[aria-haspopup="listbox"], select');
      if (!trigger || !form.contains(trigger)) return;
      window.setTimeout(() => {
        this.#syncSelectEmptyState(trigger, this.#getSelectLabelText(trigger, form));
      }, 0);
    });

    form.dataset.matchaSelectInteractions = 'true';
  }

  #refreshSelectFields(form) {
    if (!form) return;

    form.querySelectorAll('select, [role="combobox"], button[aria-haspopup="listbox"]').forEach((field) => {
      this.#prepareSelectField(field, form);
    });
  }

  #findComboboxForListbox(listbox, form) {
    if (!listbox) return null;

    const controlsId = listbox.id;
    if (controlsId) {
      const controlled = form.querySelector(
        `[role="combobox"][aria-controls="${CSS.escape(controlsId)}"], button[aria-haspopup="listbox"][aria-controls="${CSS.escape(controlsId)}"]`
      );
      if (controlled) return controlled;
    }

    let root = listbox.parentElement;
    while (root && root !== form) {
      const match = root.querySelector('[role="combobox"], button[aria-haspopup="listbox"]');
      if (match) return match;
      root = root.parentElement;
    }

    return null;
  }

  #normalizeFormsFields(form) {
    if (!form || form.dataset.matchaFieldsNormalized === 'true') return;

    this.#setupFloatingLabels(form);

    form.querySelectorAll('select, [role="combobox"], button[aria-haspopup="listbox"]').forEach((field) => {
      this.#prepareSelectField(field, form);
    });

    this.#bindSelectFieldInteractions(form);
    form.dataset.matchaFieldsNormalized = 'true';
  }

  #bindFormsEmailValidation(form) {
    if (!form || form.dataset.matchaEmailValidation === 'true') return;
    if (this.#isB2bForm()) return;

    const emailInput = form.querySelector('input[type="email"]');
    if (!emailInput) return;

    emailInput.setAttribute('inputmode', 'email');
    emailInput.setAttribute('autocomplete', 'email');

    const validate = () => {
      const value = emailInput.value.trim();
      const valid = value !== '' && this.#emailPattern.test(value);
      emailInput.setAttribute('aria-invalid', valid ? 'false' : 'true');
      return valid;
    };

    emailInput.addEventListener('blur', validate);
    emailInput.addEventListener('input', () => {
      if (emailInput.getAttribute('aria-invalid') === 'true') validate();
    });

    form.addEventListener(
      'submit',
      (event) => {
        if (!validate()) {
          event.preventDefault();
          event.stopPropagation();
          emailInput.focus();
        }
      },
      true
    );

    form.dataset.matchaEmailValidation = 'true';
  }

  #bindFormsSuccessWatch(form) {
    if (!form || form.dataset.matchaSuccessWatch === 'true') return;

    // B2B uses native Shopify Forms submit + success UI; avoid false positives here.
    if (this.#isB2bForm()) {
      form.dataset.matchaSuccessWatch = 'true';
      return;
    }

    form.addEventListener('submit', () => {
      this.#watchFormsSuccess();
    });

    form.dataset.matchaSuccessWatch = 'true';
  }

  #getResetLabel() {
    return this.dataset.resetLabel || 'Send another message';
  }

  #getSubmitLabel() {
    return this.dataset.submitLabel || 'Submit';
  }

  #isFormsSuccessState() {
    const root = this.#findFormsShadowRoot();
    if (!root) return false;

    const explicitSuccess = root.querySelector(
      '[data-testid*="success" i], [class*="FormSuccess" i], [class*="success-message" i], [class*="SuccessMessage" i]'
    );
    if (!explicitSuccess || explicitSuccess.getAttribute('hidden') === 'true') return false;

    const style = window.getComputedStyle(explicitSuccess);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return explicitSuccess.getClientRects().length > 0;
  }

  #hideShadowSubmitButtons() {
    const root = this.#findFormsShadowRoot();
    root?.querySelectorAll('button[type="submit"]').forEach((btn) => {
      this.#hideOverlayElement(btn);
    });
  }

  #switchToResetButton() {
    if (this.dataset.matchaFormSubmitted === 'true') return;
    this.dataset.matchaFormSubmitted = 'true';
    this.classList.add('matcha-contact-form--submitted');

    const wipeBtn = this.querySelector('[data-matcha-forms-submit]');
    if (!wipeBtn) return;

    const resetLabel = this.#getResetLabel();
    const newBtn = wipeBtn.cloneNode(false);
    newBtn.className = wipeBtn.className;
    newBtn.type = 'button';
    newBtn.dataset.matchaFormsSubmit = '';
    newBtn.dataset.component = 'matcha-wipe-btn';
    newBtn.innerHTML = this.#buildWipeLabel(resetLabel);
    newBtn.addEventListener('click', this.#onResetClick);
    wipeBtn.replaceWith(newBtn);

    this.#hideShadowSubmitButtons();
  }

  #onResetClick = () => {
    window.location.assign(window.location.pathname);
  };

  #watchFormsSuccess() {
    if (this.dataset.matchaFormSubmitted === 'true') return;

    if (this.#successWatchTimer) {
      clearTimeout(this.#successWatchTimer);
      this.#successWatchTimer = null;
    }

    this.#successWatchAttempts = 0;

    const tick = () => {
      if (this.dataset.matchaFormSubmitted === 'true') return;

      if (this.#isFormsSuccessState()) {
        this.#switchToResetButton();
        return;
      }

      this.#successWatchAttempts += 1;
      if (this.#successWatchAttempts < this.#maxSuccessWatchAttempts) {
        this.#successWatchTimer = setTimeout(tick, 250);
      }
    };

    this.#successWatchTimer = setTimeout(tick, 400);
  }

  #triggerFormsSubmit(form, nativeBtn) {
    if (!form) return;

    const submitBtn = form.querySelector('button[type="submit"]') || nativeBtn;

    if (submitBtn) {
      submitBtn.click();
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  }

  #upgradeFormsSubmit(form, appBlock, nativeBtn) {
    if (appBlock.dataset.matchaSubmitUpgraded === 'true') return true;

    const label = this.#getSubmitLabel();

    const wrap = document.createElement('div');
    wrap.className = 'matcha-contact-form__actions matcha-contact-form__actions--app';
    wrap.dataset.matchaFormsSubmitWrap = '';

    const wipeBtn = document.createElement('button');
    wipeBtn.type = 'button';
    wipeBtn.dataset.matchaFormsSubmit = '';
    wipeBtn.dataset.component = 'matcha-wipe-btn';
    wipeBtn.className =
      'matcha-wipe-btn matcha-wipe-btn--green matcha-wipe-btn--green-bordered button matcha-contact-form__submit';
    wipeBtn.innerHTML = this.#buildWipeLabel(label);
    wipeBtn.addEventListener('click', () => {
      if (!this.#isB2bForm()) {
        const emailInput = form.querySelector('input[type="email"]');
        if (emailInput) {
          const value = emailInput.value.trim();
          if (value === '' || !this.#emailPattern.test(value)) {
            emailInput.setAttribute('aria-invalid', 'true');
            emailInput.focus();
            return;
          }
          emailInput.removeAttribute('aria-invalid');
        }
      }

      this.#triggerFormsSubmit(form, nativeBtn);
      this.#watchFormsSuccess();
    });

    wrap.appendChild(wipeBtn);
    appBlock.appendChild(wrap);
    appBlock.dataset.matchaSubmitUpgraded = 'true';

    if (this.#isFormsSuccessState()) {
      this.#switchToResetButton();
    }

    return true;
  }

  #tryFormsSubmitUpgrade() {
    const appBlock = this.querySelector('.matcha-contact-form__app-block');
    if (!appBlock || appBlock.dataset.matchaSubmitUpgraded) return;

    const root = this.#findFormsShadowRoot();
    const form = root?.querySelector('form');
    const nativeBtn = form?.querySelector('button[type="submit"]');

    if (!form || !nativeBtn) {
      if (this.#formsRetryCount < this.#maxFormsRetries) {
        this.#formsRetryCount += 1;
        this.#scheduleFormsSubmitUpgrade(300);
      }
      return;
    }

    this.#normalizeFormsFields(form);
    this.#bindFormsEmailValidation(form);
    this.#bindFormsSuccessWatch(form);

    if (this.#isB2bForm()) {
      appBlock.dataset.matchaSubmitUpgraded = 'native';
    } else {
      this.#upgradeFormsSubmit(form, appBlock, nativeBtn);
    }

    if (!form.dataset.matchaSelectRefreshScheduled) {
      form.dataset.matchaSelectRefreshScheduled = 'true';
      [0, 300, 700, 1500, 3000].forEach((delay) => {
        window.setTimeout(() => {
          this.#refreshFloatingLabels(form);
          this.#refreshSelectFields(form);
        }, delay);
      });
    }
  }

  #scheduleFormsSubmitUpgrade(delay = 0) {
    if (this.#formsRetryTimer) clearTimeout(this.#formsRetryTimer);

    this.#formsRetryTimer = setTimeout(() => {
      this.#formsRetryTimer = null;
      this.#tryFormsSubmitUpgrade();
    }, delay);
  }
}

if (!customElements.get('matcha-contact-form')) {
  customElements.define('matcha-contact-form', MatchaContactForm);
}
